import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Trash2, Pencil, LogOut, Plus, X, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import PlansEditor from "@/components/admin/PlansEditor";
import WelcomeOfferEditor from "@/components/admin/WelcomeOfferEditor";
import ArchiveNoticeEditor from "@/components/admin/ArchiveNoticeEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_LEG = { home: "", away: "", pick: "", odds: "", ft: "", result: "WIN" };
const EMPTY = { date: "", no_double: false, legs: [{ ...EMPTY_LEG }, { ...EMPTY_LEG }] };
const EMPTY_STATS = { total_tips: "", wins: "", losses: "", win_rate: "" };
const EMPTY_FREE_TIP = { date: "", home: "", away: "", pick: "", odds: "" };
const EMPTY_BANNER = { name: "", url: "", image: "", width: "130", height: "40" };
const EMPTY_FTH = { match: "", tip: "", odds: "", result: "WIN" };

const AdminDashboard = () => {
  const { admin, logout, authHeaders } = useAuth();
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [statsForm, setStatsForm] = useState(EMPTY_STATS);
  const [statsBusy, setStatsBusy] = useState(false);
  const [freeTipForm, setFreeTipForm] = useState(EMPTY_FREE_TIP);
  const [freeTipBusy, setFreeTipBusy] = useState(false);
  const [bannersTop, setBannersTop] = useState([]);
  const [bannersBottom, setBannersBottom] = useState([]);
  const [bannersBusy, setBannersBusy] = useState(false);
  const [ftHistory, setFtHistory] = useState([]);
  const [fthForm, setFthForm] = useState(EMPTY_FTH);
  const [fthBusy, setFthBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/archive?limit=500`);
      setRows(data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/stats`);
      setStatsForm({
        total_tips: data.total_tips ?? "",
        wins: data.wins ?? "",
        losses: data.losses ?? "",
        win_rate: data.win_rate ?? "",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const loadFreeTip = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/freetip`);
      setFreeTipForm({
        date: data.date ?? "",
        home: data.home ?? "",
        away: data.away ?? "",
        pick: data.pick ?? "",
        odds: data.odds ?? "",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const loadBanners = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/banners`);
      setBannersTop(data.top || []);
      setBannersBottom(data.bottom || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadFtHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/freetips/history`);
      setFtHistory(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); loadStats(); loadFreeTip(); loadBanners(); loadFtHistory(); }, []);

  const addFtHistory = async (e) => {
    e.preventDefault();
    setFthBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/freetips/history`, fthForm, { headers: authHeaders() });
      setFtHistory((list) => [data, ...list]);
      setFthForm(EMPTY_FTH);
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setFthBusy(false);
    }
  };

  const removeFtHistory = async (row) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    try {
      await axios.delete(`${API}/admin/freetips/history/${row.id}`, { headers: authHeaders() });
      setFtHistory((list) => list.filter((x) => x.id !== row.id));
      toast.success(t.admin.deleted);
    } catch (err) {
      toast.error("Error");
    }
  };

  const saveFreeTip = async (e) => {
    e.preventDefault();
    setFreeTipBusy(true);
    try {
      await axios.put(`${API}/admin/settings/freetip`, freeTipForm, { headers: authHeaders() });
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setFreeTipBusy(false);
    }
  };

  const saveBanners = async (e) => {
    e.preventDefault();
    setBannersBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/settings/banners`,
        { top: bannersTop, bottom: bannersBottom },
        { headers: authHeaders() }
      );
      setBannersTop(data.top || []);
      setBannersBottom(data.bottom || []);
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBannersBusy(false);
    }
  };

  const updateBanner = (setter, i, field, val) => {
    setter((list) => list.map((b, idx) => (idx === i ? { ...b, [field]: val } : b)));
  };

  const moveBanner = (setter, i, dir) => {
    setter((list) => {
      const j = i + dir;
      if (j < 0 || j >= list.length) return list;
      const next = [...list];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const removeBanner = (setter, i) => {
    setter((list) => list.filter((_, idx) => idx !== i));
  };

  const addBanner = (setter) => {
    setter((list) => [...list, { ...EMPTY_BANNER }]);
  };

  const saveStats = async (e) => {
    e.preventDefault();
    setStatsBusy(true);
    try {
      await axios.put(`${API}/admin/settings/stats`, statsForm, { headers: authHeaders() });
      toast.success(t.admin.saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setStatsBusy(false);
    }
  };

  const startEdit = (row) => {
    setEditing(row.id);
    const legs = row.legs.map((l) => ({ home: l.home, away: l.away, pick: l.pick, odds: l.odds, ft: l.ft, result: l.result || "WIN" }));
    while (legs.length < 2) legs.push({ ...EMPTY_LEG });
    setForm({ date: row.date, no_double: !!row.no_double, legs });
  };

  const startNew = () => {
    setEditing("new");
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    setForm({ date: `${dd}.${mm}.${today.getFullYear()}`, no_double: false, legs: [{ ...EMPTY_LEG }, { ...EMPTY_LEG }] });
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const setLeg = (i, field, val) => {
    setForm((f) => {
      const legs = f.legs.map((l, idx) => (idx === i ? { ...l, [field]: val } : l));
      return { ...f, legs };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { date: form.date, no_double: form.no_double, legs: form.no_double ? [] : form.legs };
      if (editing === "new") {
        await axios.post(`${API}/admin/archive`, payload, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/admin/archive/${editing}`, payload, { headers: authHeaders() });
      }
      toast.success(t.admin.saved);
      cancel();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    try {
      await axios.delete(`${API}/admin/archive/${row.id}`, { headers: authHeaders() });
      toast.success(t.admin.deleted);
      setRows((r) => r.filter((x) => x.id !== row.id));
    } catch (err) {
      toast.error("Error");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white grain">
      <header className="border-b border-white/10 bg-[var(--hk-surface)] sticky top-0 z-30">
        <div className="container-hk h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-[0.15em]">
            HAKAN<span className="text-[var(--hk-red)]">.</span>TIPS
            <span className="ml-2 text-xs text-white/50 tracking-[0.3em]">// ADMIN</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-xs text-white/60">
              {t.admin.welcome}, <span className="text-white">{admin?.email}</span>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] tracking-[0.3em] font-bold hover:border-[var(--hk-red)] hover:text-[var(--hk-red)] transition-colors"
            >
              <LogOut size={14} />
              {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="container-hk py-10 md:py-14">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// ARCHIVE — DOUBLES</div>
            <h1 className="font-display text-3xl md:text-5xl uppercase tracking-tight">{t.admin.dashboard}</h1>
          </div>
          <button
            onClick={startNew}
            data-testid="admin-new-btn"
            className="btn-red px-5 py-3 text-[11px] font-bold tracking-[0.28em] flex items-center gap-2"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">{t.admin.add_new}</span>
          </button>
        </div>

        <form
          onSubmit={saveStats}
          data-testid="stats-form"
          className="border border-white/10 bg-[var(--hk-surface)] p-6 md:p-8 mb-10"
        >
          <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// SITE STATS</div>
          <p className="text-xs text-white/50 mb-6">Shown on the Archives page: Total Lifetime Tips, Wins, Losses, Win Rate.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">TOTAL LIFETIME TIPS</label>
              <input
                value={statsForm.total_tips}
                onChange={(e) => setStatsForm({ ...statsForm, total_tips: e.target.value })}
                required
                data-testid="stats-total-input"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">WINS</label>
              <input
                value={statsForm.wins}
                onChange={(e) => setStatsForm({ ...statsForm, wins: e.target.value })}
                required
                data-testid="stats-wins-input"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">LOSSES</label>
              <input
                value={statsForm.losses}
                onChange={(e) => setStatsForm({ ...statsForm, losses: e.target.value })}
                required
                data-testid="stats-losses-input"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">WIN RATE (%)</label>
              <input
                value={statsForm.win_rate}
                onChange={(e) => setStatsForm({ ...statsForm, win_rate: e.target.value })}
                required
                data-testid="stats-winrate-input"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
          </div>
          <button type="submit" disabled={statsBusy} data-testid="stats-save-btn" className="btn-red px-8 py-3 text-[11px] font-bold tracking-[0.3em]">
            {statsBusy ? "..." : t.admin.save}
          </button>
        </form>

        <ArchiveNoticeEditor />
        <PlansEditor />
        <WelcomeOfferEditor />

        <form
          onSubmit={saveFreeTip}
          data-testid="freetip-form"
          className="border border-white/10 bg-[var(--hk-surface)] p-6 md:p-8 mb-10"
        >
          <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// DAILY FREE TIP</div>
          <p className="text-xs text-white/50 mb-6">Shown on the homepage between Membership Plans and the Archive.</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">DATE (DD.MM.YYYY)</label>
              <input
                value={freeTipForm.date}
                onChange={(e) => setFreeTipForm({ ...freeTipForm, date: e.target.value })}
                required
                data-testid="ft-date"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">HOME TEAM</label>
              <input
                value={freeTipForm.home}
                onChange={(e) => setFreeTipForm({ ...freeTipForm, home: e.target.value })}
                required
                data-testid="ft-home"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">AWAY TEAM</label>
              <input
                value={freeTipForm.away}
                onChange={(e) => setFreeTipForm({ ...freeTipForm, away: e.target.value })}
                required
                data-testid="ft-away"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">TIP</label>
              <input
                value={freeTipForm.pick}
                onChange={(e) => setFreeTipForm({ ...freeTipForm, pick: e.target.value })}
                required
                data-testid="ft-pick"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">ODDS</label>
              <input
                value={freeTipForm.odds}
                onChange={(e) => setFreeTipForm({ ...freeTipForm, odds: e.target.value })}
                required
                data-testid="ft-odds"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
          </div>
          <button type="submit" disabled={freeTipBusy} data-testid="ft-save" className="btn-red px-8 py-3 text-[11px] font-bold tracking-[0.3em]">
            {freeTipBusy ? "..." : t.admin.save}
          </button>
        </form>

        <div className="border border-white/10 bg-[var(--hk-surface)] p-6 md:p-8 mb-10">
          <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// PREVIOUS FREE TIPS</div>
          <p className="text-xs text-white/50 mb-6">Shown under the Daily Free Tip on the homepage. One line per entry: Match | Tip | Odds | Result.</p>

          <form onSubmit={addFtHistory} data-testid="fth-add-form" className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 mb-8">
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">MATCH</label>
              <input
                value={fthForm.match}
                onChange={(e) => setFthForm({ ...fthForm, match: e.target.value })}
                required
                placeholder="Galatasaray - Fenerbahce"
                data-testid="fth-match"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">TIP</label>
              <input
                value={fthForm.tip}
                onChange={(e) => setFthForm({ ...fthForm, tip: e.target.value })}
                required
                placeholder="Over 2.5"
                data-testid="fth-tip"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">ODDS</label>
              <input
                value={fthForm.odds}
                onChange={(e) => setFthForm({ ...fthForm, odds: e.target.value })}
                required
                placeholder="1.85"
                data-testid="fth-odds"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">RESULT</label>
              <select
                value={fthForm.result}
                onChange={(e) => setFthForm({ ...fthForm, result: e.target.value })}
                data-testid="fth-result"
                className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              >
                <option value="WIN">WIN</option>
                <option value="LOSS">LOSS</option>
              </select>
            </div>
            <div className="flex items-end col-span-2 md:col-span-1">
              <button type="submit" disabled={fthBusy} data-testid="fth-add-btn" className="btn-red w-full px-6 py-3 text-[11px] font-bold tracking-[0.3em] flex items-center justify-center gap-2">
                <Plus size={13} /> {fthBusy ? "..." : "ADD"}
              </button>
            </div>
          </form>

          <div className="space-y-2">
            {ftHistory.map((row) => (
              <div key={row.id} data-testid={`fth-row-${row.id}`} className="border border-white/10 bg-black px-4 py-3 flex items-center gap-4">
                <span className="flex-1 min-w-0 text-sm truncate">{row.match}</span>
                <span className="text-xs text-white/60 whitespace-nowrap">{row.tip}</span>
                <span className="font-mono text-xs text-white/70 whitespace-nowrap">{row.odds}</span>
                <span className={`px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] ${row.result === "WIN" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[var(--hk-red)]/15 text-[var(--hk-red)]"}`}>
                  {row.result}
                </span>
                <button
                  type="button"
                  onClick={() => removeFtHistory(row)}
                  data-testid={`fth-delete-${row.id}`}
                  className="p-2 border border-white/10 hover:border-[var(--hk-red)] hover:text-[var(--hk-red)] transition-colors shrink-0"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            {ftHistory.length === 0 && (
              <div className="p-6 text-center text-white/40 border border-dashed border-white/10 text-xs tracking-[0.2em]">
                NO PREVIOUS FREE TIPS YET — ADD ONE ABOVE
              </div>
            )}
          </div>
        </div>

        <form
          onSubmit={saveBanners}
          data-testid="banners-form"
          className="border border-white/10 bg-[var(--hk-surface)] p-6 md:p-8 mb-10"
        >
          <div className="text-[10px] tracking-[0.35em] text-[var(--hk-red)] font-bold mb-2">// BANNERS</div>
          <p className="text-xs text-white/50 mb-8">Partner banners shown above and below the Daily Free Tip. Default size 130x40 px.</p>

          <BannerEditor
            title="ABOVE FREE TIP"
            placement="top"
            items={bannersTop}
            setter={setBannersTop}
            updateBanner={updateBanner}
            moveBanner={moveBanner}
            removeBanner={removeBanner}
            addBanner={addBanner}
          />

          <div className="border-t border-white/10 my-10" />

          <BannerEditor
            title="BELOW FREE TIP"
            placement="bottom"
            items={bannersBottom}
            setter={setBannersBottom}
            updateBanner={updateBanner}
            moveBanner={moveBanner}
            removeBanner={removeBanner}
            addBanner={addBanner}
          />

          <button type="submit" disabled={bannersBusy} data-testid="banners-save" className="btn-red px-8 py-3 text-[11px] font-bold tracking-[0.3em] mt-8">
            {bannersBusy ? "..." : t.admin.save}
          </button>
        </form>

        {editing && (
          <form
            onSubmit={save}
            data-testid="admin-form"
            className="border border-[var(--hk-red)] bg-[var(--hk-surface)] p-6 md:p-8 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs tracking-[0.3em] text-[var(--hk-red)] font-bold">
                {editing === "new" ? "NEW DOUBLE" : `EDIT ${editing.slice(0, 6)}...`}
              </div>
              <button type="button" onClick={cancel} className="text-white/50 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="mb-6 max-w-xs">
              <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">DATE (DD.MM.YYYY)</label>
              <input
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                placeholder="02.08.2026"
                data-testid="f-date"
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>

            <label
              data-testid="f-no-double"
              className={`mb-6 flex items-center gap-3 border px-4 py-3 cursor-pointer select-none transition-colors ${
                form.no_double ? "border-[var(--hk-red)] bg-[var(--hk-red)]/10" : "border-white/15 hover:border-white/40"
              }`}
            >
              <input
                type="checkbox"
                checked={form.no_double}
                onChange={(e) => setForm({ ...form, no_double: e.target.checked })}
                data-testid="f-no-double-checkbox"
                className="accent-[var(--hk-red)] w-4 h-4"
              />
              <span className="text-[11px] font-bold tracking-[0.25em]">NO SUBSCRIPTION DOUBLE</span>
              <span className="text-[11px] text-white/50">— no matches published this day (no good betting opportunities)</span>
            </label>

            {!form.no_double &&
              form.legs.map((leg, i) => (
                <div key={i} className="mb-6 border-t border-white/10 pt-4">
                  <div className="text-[10px] tracking-[0.3em] text-white/50 mb-3">MATCH {i + 1}</div>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <LegField label="Home Team" value={leg.home} onChange={(v) => setLeg(i, "home", v)} testid={`f${i}-home`} span={2} />
                    <LegField label="Away Team" value={leg.away} onChange={(v) => setLeg(i, "away", v)} testid={`f${i}-away`} span={2} />
                    <LegField label="Pick (1, 2, X, Over 2.5, 1/1, BTS-YES...)" value={leg.pick} onChange={(v) => setLeg(i, "pick", v)} testid={`f${i}-pick`} span={2} />
                    <LegField label="Odds" value={leg.odds} onChange={(v) => setLeg(i, "odds", v)} testid={`f${i}-odds`} />
                    <LegField label="Final (2:1 or HT/FT 1:0 / 2:0)" value={leg.ft} onChange={(v) => setLeg(i, "ft", v)} testid={`f${i}-ft`} span={2} />
                    <div>
                      <label className="block text-[10px] tracking-[0.25em] text-white/50 mb-2">RESULT</label>
                      <div className="flex border border-white/15" role="group" data-testid={`f${i}-result`}>
                        <button
                          type="button"
                          onClick={() => setLeg(i, "result", "WIN")}
                          data-testid={`f${i}-result-win`}
                          className={`flex-1 py-3 text-[10px] font-bold tracking-[0.2em] transition-colors ${
                            leg.result === "WIN" ? "bg-[#10B981] text-black" : "text-white/50 hover:text-white"
                          }`}
                        >
                          WIN
                        </button>
                        <button
                          type="button"
                          onClick={() => setLeg(i, "result", "LOSS")}
                          data-testid={`f${i}-result-loss`}
                          className={`flex-1 py-3 text-[10px] font-bold tracking-[0.2em] transition-colors ${
                            leg.result === "LOSS" ? "bg-[var(--hk-red)] text-white" : "text-white/50 hover:text-white"
                          }`}
                        >
                          LOSE
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            <div className="flex gap-3 max-w-md">
              <button type="submit" disabled={busy} data-testid="admin-save" className="btn-red flex-1 py-3 text-[11px] font-bold tracking-[0.3em]">
                {busy ? "..." : t.admin.save}
              </button>
              <button type="button" onClick={cancel} className="btn-outline flex-1 py-3 text-[11px] font-bold tracking-[0.3em]">
                {t.admin.cancel}
              </button>
            </div>
            <p className="mt-3 text-[11px] text-white/40">Choose WIN or LOSE for each match yourself — results are never calculated automatically.</p>
          </form>
        )}

        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.id} data-testid={`admin-row-${row.id}`} className="border border-white/10 bg-black p-4 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm text-[var(--hk-red)] font-semibold mb-2">{row.date}</div>
                <div className="space-y-1">
                  {row.no_double && (
                    <span data-testid={`admin-no-double-${row.id}`} className="inline-flex px-2 py-0.5 text-[9px] font-bold tracking-[0.2em] border border-white/15 bg-white/[0.04] text-white/60">
                      NO SUBSCRIPTION DOUBLE
                    </span>
                  )}
                  {row.legs.map((l, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <span className="text-white truncate">{l.home} <span className="text-white/40">vs</span> {l.away}</span>
                      <span className="text-white/50 text-xs">{l.pick} @ {l.odds}</span>
                      <span className="font-mono text-white/70 text-xs">{l.ft}</span>
                      <span className={`px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] ${l.result === "WIN" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[var(--hk-red)]/15 text-[var(--hk-red)]"}`}>{l.result}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => startEdit(row)} data-testid={`edit-${row.id}`} className="p-2 border border-white/10 hover:border-white transition-colors">
                  <Pencil size={13} />
                </button>
                <button onClick={() => remove(row)} data-testid={`delete-${row.id}`} className="p-2 border border-white/10 hover:border-[var(--hk-red)] hover:text-[var(--hk-red)] transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-10 text-center text-white/50">No results yet.</div>}
        </div>
      </main>
    </div>
  );
};

const BannerEditor = ({ title, placement, items, setter, updateBanner, moveBanner, removeBanner, addBanner }) => (
  <div data-testid={`banners-${placement}-editor`}>
    <div className="flex items-center justify-between mb-4">
      <div className="text-[10px] tracking-[0.3em] text-white/50">
        {title} — {items.length} BANNER{items.length === 1 ? "" : "S"}
      </div>
      <button
        type="button"
        onClick={() => addBanner(setter)}
        data-testid={`banners-${placement}-add`}
        className="btn-outline px-4 py-2 text-[10px] font-bold tracking-[0.25em] flex items-center gap-2"
      >
        <Plus size={12} /> ADD BANNER
      </button>
    </div>
    <div className="space-y-4">
      {items.map((b, i) => (
        <div key={i} data-testid={`banner-${placement}-${i}`} className="border border-white/10 bg-black p-4 flex gap-3">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                value={b.name}
                onChange={(e) => updateBanner(setter, i, "name", e.target.value)}
                placeholder="Banner Name"
                data-testid={`banner-${placement}-${i}-name`}
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
              <input
                value={b.url}
                onChange={(e) => updateBanner(setter, i, "url", e.target.value)}
                placeholder="https://link-url.com/"
                data-testid={`banner-${placement}-${i}-url`}
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div className="flex items-center gap-3">
              {b.image ? <img src={b.image} alt="" className="h-10 w-auto border border-white/10 shrink-0" /> : null}
              <input
                value={b.image}
                onChange={(e) => updateBanner(setter, i, "image", e.target.value)}
                placeholder="https://image-url.gif"
                data-testid={`banner-${placement}-${i}-image`}
                className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                value={b.width}
                onChange={(e) => updateBanner(setter, i, "width", e.target.value)}
                placeholder="130"
                data-testid={`banner-${placement}-${i}-width`}
                className="w-24 bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
              <input
                value={b.height}
                onChange={(e) => updateBanner(setter, i, "height", e.target.value)}
                placeholder="40"
                data-testid={`banner-${placement}-${i}-height`}
                className="w-24 bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
              />
              <span className="text-[10px] tracking-[0.25em] text-white/40">#{i + 1} OF {items.length}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              type="button"
              onClick={() => moveBanner(setter, i, -1)}
              disabled={i === 0}
              data-testid={`banner-${placement}-${i}-up`}
              className="p-2 border border-white/10 hover:border-white transition-colors disabled:opacity-30"
            >
              <ArrowUp size={13} />
            </button>
            <button
              type="button"
              onClick={() => moveBanner(setter, i, 1)}
              disabled={i === items.length - 1}
              data-testid={`banner-${placement}-${i}-down`}
              className="p-2 border border-white/10 hover:border-white transition-colors disabled:opacity-30"
            >
              <ArrowDown size={13} />
            </button>
            <button
              type="button"
              onClick={() => removeBanner(setter, i)}
              data-testid={`banner-${placement}-${i}-delete`}
              className="p-2 border border-white/10 hover:border-[var(--hk-red)] hover:text-[var(--hk-red)] transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
      {items.length === 0 && (
        <div className="p-6 text-center text-white/40 border border-dashed border-white/10 text-xs tracking-[0.2em]">
          NO BANNERS YET — ADD ONE
        </div>
      )}
    </div>
  </div>
);

const LegField = ({ label, value, onChange, testid, span = 1 }) => (
  <div className={span === 2 ? "md:col-span-2" : ""}>
    <label className="block text-[10px] tracking-[0.15em] text-white/50 mb-2">{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      data-testid={testid}
      className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[var(--hk-red)] transition-colors"
    />
  </div>
);

export default AdminDashboard;
